import type { RefObject } from 'react';
import React from 'react';

import type { ButtonProps } from '../Button.types';

export type UseButtonActionsProps = Pick<
    ButtonProps,
    'onClick' | 'onMouseDown' | 'onMouseUp' | 'isLoading' | 'disableFocusOnClick'
> & {
    ref: RefObject<HTMLElement>;
};

export const useButtonActions = (props: UseButtonActionsProps) => {
    const { onClick, onMouseDown, onMouseUp, isLoading, ref, disableFocusOnClick } = props;

    const handleClick = React.useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            if (ref.current && !disableFocusOnClick) {
                // Выставляем программно focus, т.к. в браузерах Safari и Firefox
                // клик по кнопке не выставляет фокус автоматически.
                ref.current.focus();
            }

            if (!isLoading) {
                onClick?.(event);
            }
        },
        [ref, disableFocusOnClick, isLoading, onClick],
    );

    const handleMouseDown = React.useMemo(() => {
        if (!disableFocusOnClick && !onMouseDown) {
            return;
        }

        return (event: React.MouseEvent<HTMLButtonElement>) => {
            if (disableFocusOnClick) {
                event.preventDefault();
            }

            onMouseDown?.(event);
        };
    }, [disableFocusOnClick, onMouseDown]);

    const handleMouseUp = React.useMemo(() => {
        if (!disableFocusOnClick && !onMouseUp) {
            return;
        }

        return (event: React.MouseEvent<HTMLButtonElement>) => {
            if (disableFocusOnClick) {
                event.preventDefault();
            }

            onMouseUp?.(event);
        };
    }, [disableFocusOnClick, onMouseUp]);

    return { handleClick, handleMouseDown, handleMouseUp };
};
